import CopyIcon from "@assets/icons/duplicate.svg";
import { useI18n } from "@core/i18n/i18n";
import { patientManager } from "@core/service-container";
import { PendingPatient } from "@domain/patient/patient";
import { PatientNotPendingError } from "@domain/patient/patient-errors";
import { e2eTestId } from "@e2e/test-ids";
import styled from "@emotion/styled";
import { ButtonGroup } from "@ui/components/buttons/button-group";
import { ToastContext } from "@ui/components/common/toast-provider";
import { ComponentToPrint } from "@ui/pages/home/pending-patient-dialog/components/component-to-print";
import { ShareUsingPrintForm } from "@ui/pages/home/pending-patient-dialog/components/share-using-print-form";
import { SharingUsingEmailForm } from "@ui/pages/home/pending-patient-dialog/components/sharing-using-email-form";
import { stack } from "@ui/style";
import { colors } from "@ui/style/colors";
import React, { useCallback, useContext, useMemo, useRef, useState } from "react";
import { useReactToPrint } from "react-to-print";

export enum InvitationCodeSharingMethod {
	EMAIL = "email",
	PRINT = "print",
}

export type InvitationSentData =
	| {
			sharingMethod: InvitationCodeSharingMethod.PRINT;
	  }
	| {
			sharingMethod: InvitationCodeSharingMethod.EMAIL;
			email: string;
	  };

export const PendingPatientCreated: React.FC<{
	pendingPatient: PendingPatient;
	previousInvitationSentData?: InvitationSentData;
	onInvitationCodeSent?: (data: InvitationSentData) => void;
}> = ({ pendingPatient, previousInvitationSentData, onInvitationCodeSent }) => {
	const { format } = useI18n();
	const { showToast } = useContext(ToastContext);
	const [selectedSharingMethod, setSelectedSharingMethod] = useState(
		previousInvitationSentData?.sharingMethod ?? InvitationCodeSharingMethod.EMAIL,
	);
	const [hasCopiedCode, setHasCopiedCode] = useState(false);

	const componentRef = useRef(null);

	const handlePrint = useReactToPrint({
		content: () => componentRef.current,
		onAfterPrint: () => onInvitationCodeSent?.({ sharingMethod: InvitationCodeSharingMethod.PRINT }),
	});

	const handleEmail = useCallback(
		async (email: string) => {
			try {
				await patientManager.sendInvitationCodeEmail(pendingPatient.id, email);
			} catch (e) {
				if (e instanceof PatientNotPendingError) {
					showToast(
						format("home.pendingPatientDialog.details.sharing.email.patientNotPending.title"),
						format("home.pendingPatientDialog.details.sharing.email.patientNotPending.description"),
					);
					return;
				}
				throw e;
			}
			onInvitationCodeSent?.({ sharingMethod: InvitationCodeSharingMethod.EMAIL, email: email });
		},
		[onInvitationCodeSent, pendingPatient.id, showToast, format],
	);

	const copyInvitationCodeToClipboard = useCallback(() => {
		navigator.clipboard.writeText(pendingPatient.activationCode);
		setHasCopiedCode(true);
	}, [pendingPatient.activationCode]);

	const sharingOptions = useMemo<{ title: string; data: InvitationCodeSharingMethod }[]>(
		() => [
			{ title: format("home.pendingPatientDialog.details.sharing.email"), data: InvitationCodeSharingMethod.EMAIL },
			{ title: format("home.pendingPatientDialog.details.sharing.print"), data: InvitationCodeSharingMethod.PRINT },
		],
		[format],
	);

	return (
		<>
			<div>{format("home.pendingPatientDialog.details.description")}</div>
			<Section>
				<div className="p-size-l-bold">{format("home.pendingPatientDialog.details.code.title")}</div>
				<InvitationCodeContainer data-test={e2eTestId.createPatientDialog.invitationCode}>
					<InvitationCode className="p-size-l-bold" highlighted={hasCopiedCode}>
						{pendingPatient.activationCode}
					</InvitationCode>
					<DuplicateIconContainer onClick={copyInvitationCodeToClipboard}>
						<CopyIcon width="20" height="20" />
					</DuplicateIconContainer>
				</InvitationCodeContainer>
			</Section>
			<Section>
				<div className="p-size-l-bold">{format("home.pendingPatientDialog.details.sharing.title")}</div>
				<ButtonGroup
					style={{ display: "grid", gridAutoFlow: "column" }}
					options={sharingOptions}
					value={selectedSharingMethod}
					onChange={newSharingMethod => setSelectedSharingMethod(newSharingMethod)}
				/>
			</Section>
			<div style={{ display: "none" }}>
				<ComponentToPrint code={pendingPatient.activationCode} url={pendingPatient.link} ref={componentRef} />
			</div>
			{selectedSharingMethod === InvitationCodeSharingMethod.PRINT ? (
				<ShareUsingPrintForm onSuccess={handlePrint} />
			) : (
				<SharingUsingEmailForm
					defaultEmail={
						previousInvitationSentData?.sharingMethod === InvitationCodeSharingMethod.EMAIL
							? previousInvitationSentData.email
							: pendingPatient.email
					}
					onSuccess={handleEmail}
				/>
			)}
		</>
	);
};

const Section = styled.div`
	${stack("S", undefined, "stretch")}
`;

const DuplicateIconContainer = styled.div`
	cursor: pointer;
	width: 40px;
	height: 40px;
	border-radius: 50%;
	display: grid;
	place-content: center;
	color: ${colors.grey};
	transition:
		color 0.2s,
		background-color 0.2s;

	:hover {
		background-color: ${colors.primary10};

		:active {
			background-color: ${colors.primary25};
			color: ${colors.primary};
		}
	}
`;

const InvitationCode = styled.div<{ highlighted: boolean }>`
	transition: background-color 0.2s;
	background-color: ${({ highlighted }) => (highlighted ? colors.primary25 : "unset")};
	padding: 4px 8px;
`;

const InvitationCodeContainer = styled.div`
	background: ${colors.primaryBackground};
	border-radius: var(--border-radius);
	color: ${colors.primary};
	padding: 16px 8px;
	display: grid;
	grid-template-columns: 1fr auto;
	place-items: center;
`;
